<template>
    <CompassModal
        v-model:visible="showModal"
        class="my-activities-card-config"
        size="small"
        :title="$t.__('common.actions.configure')"
        :primaryAction="primaryAction"
        :secondaryActions="[
            {
                label: $t.__('common.actions.cancel'),
                action: () => $emit('close'),
            },
        ]"
        @closed="$emit('close')"
    >
        <p class="typographies-paragraph-medium-regular my-activities-card-config__description">
            {{ $t.__('dashboard.cards.MyActivitiesCardConfigDialog.description_dialog') }}
        </p>
        <CompassFormItem
            :label="$t.__('dashboard.cards.MyActivitiesCardConfigDialog.activity_label')"
            class="my-activities-card-config__select-spacing"
        >
            <CompassSelect
                v-model="filterByLabel.selectedOptions"
                :placeholder="$t.__('common.actions.select')"
                multiple
                menuClass="my-activities-card-config__select-menu"
                class="my-activities-card-config__select"
            >
                <CompassDropdownOption
                    v-for="option in filterByLabel.options"
                    v-bind="option"
                    :key="option.id"
                    :value="option.id"
                />
            </CompassSelect>
        </CompassFormItem>
        <CompassFormItem
            :label="$t.__('dashboard.cards.MyActivitiesCardConfigDialog.activity_type_label')"
        >
            <CompassSelect
                v-model="filterByType.selectedOptions"
                :placeholder="$t.__('common.actions.select')"
                multiple
                menuClass="my-activities-card-config__select-menu"
                class="my-activities-card-config__select"
            >
                <CompassDropdownOption
                    v-for="option in filterByType.options"
                    v-bind="option"
                    :key="option.id"
                    :value="option.id"
                />
            </CompassSelect>
        </CompassFormItem>
    </CompassModal>
</template>
<script>
import {
    getSelectedLabels,
    initializeMetasLabels,
    initLabelsFilter,
} from 'Helpers/activities/activitiesLabelsFilterHelper';
import { createNamespacedHelpers } from 'vuex';

const { mapActions, mapGetters: activitiesMapGetters } =
    createNamespacedHelpers('activitiesStores');
export default {
    props: {
        cardName: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            showModal: false,
            metasLabels: {},
            filterByLabel: {
                code: 'labels',
                options: [],
                selectedOptions: [],
                value: [],
            },
            filterByType: {
                code: 'entities',
                options: [
                    { label: this.$l10n.__('Tâches'), id: 'task', icon: 'obj-slsy-task' },
                    { label: this.$l10n.__('Appels'), id: 'phonecall', icon: 'obj-slsy-phone' },
                ],
                selectedOptions: [],
                value: ['task', 'phonecall'],
            },
        };
    },
    computed: {
        ...activitiesMapGetters({ metas: 'getMetas', appliedFilters: 'getFilters' }),
        primaryAction() {
            return {
                label: this.$t.__('common.actions.confirm'),
                action: this.confirmConfig,
                loading: this.isSubmitting,
            };
        },
    },
    async created() {
        this.metasLabels = initializeMetasLabels(this.metas);
        await initLabelsFilter(this.metasLabels, this.filterByLabel);
        this.setFilterByLabelValues();
        this.initSelectedOptions();
    },
    mounted() {
        this.showModal = true;
    },
    methods: {
        ...mapActions(['updateFilters']),
        async confirmConfig() {
            this.isSubmitting = true;
            try {
                const filters = {
                    ...this.appliedFilters,
                    [this.filterByType.code]: this.filterByType.selectedOptions,
                    [this.filterByLabel.code]: getSelectedLabels(
                        this.filterByLabel.selectedOptions,
                        this.filterByLabel,
                        this.metasLabels,
                    ),
                };

                if (this.filterByLabel.selectedOptions.length === 0) {
                    delete filters[this.filterByLabel.code];
                }

                if (this.filterByType.selectedOptions.length === 0) {
                    delete filters[this.filterByType.code];
                }

                await this.updateFilters(filters);

                this.$emit('close');
                EventBus.emit('activities-listing-table:refresh');
            } catch {
                this.$pushToast({
                    text: this.$t.__('common.labels.filters_update_error'),
                    type: 'error',
                    icon: true,
                });
            } finally {
                this.isSubmitting = false;
            }
        },
        setFilterByLabelValues() {
            const categorizedIds = {
                phonecalls: [],
                tasks: [],
            };

            for (const [key, labels] of Object.entries(this.metasLabels)) {
                for (const label of labels) {
                    if (key === 'phonecall') {
                        categorizedIds.phonecalls.push(label.id);
                    } else if (key === 'tasks') {
                        categorizedIds.tasks.push(label.id);
                    }
                }
            }

            this.filterByLabel.value = categorizedIds;
        },
        initSelectedOptions() {
            const taskLabels = this.appliedFilters.labels?.tasks ?? [];
            const phonecallLabels = this.appliedFilters.labels?.phonecalls ?? [];
            this.filterByType.selectedOptions = this.appliedFilters.entities ?? [];

            for (const taskId of taskLabels) {
                Object.values(this.metasLabels.tasks).find((taskLabel) => {
                    if (taskLabel.id === taskId) {
                        this.filterByLabel.selectedOptions.push(taskLabel.label);
                    }
                });
            }

            for (const phonecallId of phonecallLabels) {
                Object.values(this.metasLabels.phonecall).find((phonecallLabel) => {
                    if (phonecallLabel.id === phonecallId) {
                        this.filterByLabel.selectedOptions.push(phonecallLabel.label);
                    }
                });
            }
        },
    },
};
</script>
<style lang="scss" scoped>
.my-activities-card-config {
    .compass-modal__modal__content {
        .my-activities-card-config__description {
            margin: 0 0 var(--spacing-2);
        }

        .my-activities-card-config__select-spacing {
            margin-bottom: var(--spacing-4);
        }

        .my-activities-card-config__select {
            width: 50%;
        }
    }
}

:deep(.compass-select__tag-container) {
    max-height: 312px;
    overflow-y: auto;
    overflow-x: hidden;
}
</style>
<style lang="scss">
#compass-dropdown-container {
    .my-activities-card-config__select-menu {
        width: 200px;
        z-index: 1001;
    }
}
</style>
